/**
 * Monte Carlo simulation over the planning engine.
 *
 * ── What this is ───────────────────────────────────────────────────────────
 * `runPlanningEngineWithBands` already answers the confidence question at two
 * points: an optimistic pass (unbuffered pools, raw estimates) and a
 * conservative one (buffered pools, demand x an uncertainty multiplier). Two
 * points is a band. This module is the same idea sampled: draw the multipliers
 * from distributions N times, run the SAME pure engine per draw, and report the
 * frequency with which each priority carried.
 *
 * The answer changes shape as a result. Instead of "firm / at risk / out" it is
 * "this priority carries in 92% of futures", and instead of one scope number it
 * is a distribution — P50 and P80 scope, which is what a commitment is actually
 * made against.
 *
 * ── Three properties this deliberately has ─────────────────────────────────
 * 1. **No new arithmetic.** Every trial is `runPlanningEngine` on a perturbed
 *    copy of the input. Nothing here re-implements capacity, dependencies,
 *    qualification or participation, so a change to the engine changes the
 *    simulation for free and the two can never disagree.
 *
 * 2. **Seeded and reproducible.** The PRNG is explicit and the whole run is a
 *    pure function of `(input, config)`. The same scenario simulated twice
 *    gives the same probabilities — which is the difference between a number
 *    somebody can commit to and a number that moves when you look at it.
 *
 * 3. **Correlated draws.** Estimate overruns and capacity shortfalls are not
 *    independent events: the quarter that goes badly goes badly for everything
 *    at once. Sampling each item independently is the classic Monte Carlo error
 *    — the aggregate tightens toward the mean by the central limit theorem and
 *    the simulation reports a confidence nobody should have. Every draw here
 *    loads onto a shared factor (`correlation`), so bad futures are bad
 *    together and the tail keeps its weight.
 *
 * NO Supabase imports, no React. Pure functions only, except the `generatedAt`
 * stamp on the finished result.
 */

import { runPlanningEngine } from './planningEngineModule';
import type {
  CapacityModelRow,
  ParticipationRequestStatus,
  ParticipationTristate,
  PlanningEngineInput,
  PlanningEngineOutput,
  Priority,
  PriorityCapacityRow,
  PrioritySummary,
  QualifiedSupplyUnit,
  TeamParticipation,
  WorkUnit,
} from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// §1 — Configuration
// ---------------------------------------------------------------------------

export interface MonteCarloConfig {
  /** Futures to sample. */
  iterations: number;
  /** PRNG seed — the same seed always produces the same result. */
  seed: number;
  /**
   * Spread (log-normal sigma) applied to every item's demand before signals
   * widen it. 0.20 is roughly "a typical estimate lands within +/-20%".
   */
  baseSpread: number;
  /**
   * How hard the per-item uncertainty signals (decomposition maturity,
   * assumption flags, risk level, confidence) widen the spread. 0 ignores them
   * and simulates one uniform spread across the portfolio.
   */
  signalWeight: number;
  /**
   * Systematic optimism. 1.0 = estimates are unbiased; 1.15 = the portfolio
   * has historically come in 15% over. This shifts the MEAN of the demand
   * draw, where spread only widens it.
   */
  demandBias: number;
  /** Log-normal sigma on pool availability. Capacity is a forecast too. */
  capacitySpread: number;
  /**
   * How much of each draw comes from the shared factor rather than the item's
   * own luck, 0–1. At 0 every item is independent and the portfolio total is
   * implausibly tight; at 1 everything moves in lockstep.
   */
  correlation: number;
  /** Resolve unconfirmed cross-team participation stochastically per trial. */
  participationRisk: boolean;
}

export const DEFAULT_MONTE_CARLO_CONFIG: MonteCarloConfig = {
  iterations: 500,
  seed: 1,
  baseSpread: 0.2,
  signalWeight: 1,
  demandBias: 1,
  capacitySpread: 0.1,
  correlation: 0.35,
  participationRisk: true,
};

/** At or above this probability of carrying, a priority reads as firm. */
export const FIRM_PROBABILITY = 0.85;
/** Below this, it reads as out. Between the two it is at risk. */
export const OUT_PROBABILITY = 0.15;

/** Largest sigma any single item may reach, however many signals stack up. */
const MAX_SPREAD = 1.2;

// ---------------------------------------------------------------------------
// §2 — Seeded randomness
// ---------------------------------------------------------------------------

/**
 * mulberry32 — small, fast, and good enough for planning-grade sampling.
 * Chosen over `Math.random` for the reproducibility property above, not for
 * statistical strength; nothing here is cryptographic.
 */
export function createRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Box–Muller. One of the pair is discarded; caching it is not worth the state. */
function standardNormal(rng: () => number): number {
  const u = Math.max(rng(), Number.EPSILON);
  const v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * A log-normal multiplier with arithmetic mean `mean` and log-sigma `sigma`,
 * driven by an already-drawn standard normal so the caller controls correlation.
 *
 * The -sigma^2/2 term is what keeps the mean at `mean`. Without it a wider
 * spread would quietly inflate expected demand, and raising the spread slider
 * would look like it was also raising every estimate.
 */
function logNormalFrom(z: number, mean: number, sigma: number): number {
  if (sigma <= 0) return mean;
  return Math.exp(Math.log(mean) - (sigma * sigma) / 2 + sigma * z);
}

// ---------------------------------------------------------------------------
// §3 — Per-item spread, derived from signals already in the model
// ---------------------------------------------------------------------------

/**
 * Additive spread contributions. These are judgments, not measurements — the
 * workspace has no estimate-vs-actual history to calibrate against yet. They
 * live here as named constants so they can be argued with, and so the day
 * historical calibration exists it replaces this table rather than the engine.
 */
export const SPREAD_SIGNALS = {
  /** An estimate on work nobody has broken down is a guess about a guess. */
  notDecomposed: 0.35,
  partiallyDecomposed: 0.15,
  /** The estimate explicitly rests on an assumption. */
  assumptionFlag: 0.25,
  riskHigh: 0.25,
  riskMedium: 0.1,
  /** Work not yet started can still surprise; work in flight much less so. */
  backlog: 0.1,
  ready: 0.05,
  blocked: 0.15,
  /** Scaled by (1 - confidence) when the priority carries a confidence score. */
  lowConfidence: 0.3,
} as const;

/** Normalises a confidence field that may be stored 0–1 or 0–100. */
function normalizedConfidence(raw: number | null): number | null {
  if (raw == null || !Number.isFinite(raw)) return null;
  if (raw > 1) return Math.min(raw / 100, 1);
  return Math.max(raw, 0);
}

/** How uncertain this work unit's estimate is, as a log-normal sigma. */
export function workUnitSpread(wu: WorkUnit, config: MonteCarloConfig): number {
  // Finished work is not a forecast.
  if (wu.state === 'done' || wu.state === 'cancelled') return 0;

  let signal = 0;
  if (wu.assumptionFlag) signal += SPREAD_SIGNALS.assumptionFlag;

  const risk = (wu.riskLevel ?? '').toLowerCase();
  if (risk === 'high' || risk === 'critical') signal += SPREAD_SIGNALS.riskHigh;
  else if (risk === 'medium') signal += SPREAD_SIGNALS.riskMedium;

  if (wu.state === 'backlog') signal += SPREAD_SIGNALS.backlog;
  else if (wu.state === 'ready' || wu.state === 'selected_for_dev') signal += SPREAD_SIGNALS.ready;
  else if (wu.state === 'blocked') signal += SPREAD_SIGNALS.blocked;

  return Math.min(config.baseSpread + signal * config.signalWeight, MAX_SPREAD);
}

/** How uncertain this priority's own directional estimate is. */
export function prioritySpread(priority: Priority, config: MonteCarloConfig): number {
  let signal = 0;
  if (priority.workDefinitionStatus === 'not_decomposed') signal += SPREAD_SIGNALS.notDecomposed;
  else if (priority.workDefinitionStatus === 'partially_decomposed') {
    signal += SPREAD_SIGNALS.partiallyDecomposed;
  }

  const confidence = normalizedConfidence(priority.confidence);
  if (confidence != null) signal += (1 - confidence) * SPREAD_SIGNALS.lowConfidence;

  return Math.min(config.baseSpread + signal * config.signalWeight, MAX_SPREAD);
}

// ---------------------------------------------------------------------------
// §4 — Participation
// ---------------------------------------------------------------------------

/** Probability the request itself lands, before the tristate refines it. */
const REQUEST_STATUS_PROBABILITY: Record<ParticipationRequestStatus, number> = {
  confirmed: 0.97,
  tentative: 0.75,
  requested: 0.55,
  declined: 0.1,
};

/** What the current answer on a dimension says about it holding. */
const TRISTATE_FACTOR: Record<ParticipationTristate, number> = {
  yes: 1,
  partial: 0.6,
  no: 0.25,
};

/**
 * Probability one participation dimension resolves "yes" this trial.
 *
 * `commonShift` is the shared-conditions penalty: in a future where estimates
 * overran, shared teams were also likelier to be pulled away. Same factor, same
 * direction — which is exactly the correlation that makes the tail honest.
 */
export function participationProbability(
  requestStatus: ParticipationRequestStatus,
  current: ParticipationTristate,
  commonShift: number,
): number {
  const base = REQUEST_STATUS_PROBABILITY[requestStatus] ?? 0.5;
  const p = base * (TRISTATE_FACTOR[current] ?? 0.5) - commonShift;
  return Math.min(Math.max(p, 0), 1);
}

function sampleParticipation(
  rows: TeamParticipation[],
  rng: () => number,
  commonShift: number,
): TeamParticipation[] {
  return rows.map((row) => {
    const draw = (current: ParticipationTristate): ParticipationTristate =>
      rng() < participationProbability(row.requestStatus, current, commonShift) ? 'yes' : 'no';
    return {
      ...row,
      capacityConfirmed: draw(row.capacityConfirmed),
      capabilityPresent: draw(row.capabilityPresent),
      committed: draw(row.committed),
    };
  });
}

// ---------------------------------------------------------------------------
// §5 — Building one sampled input
// ---------------------------------------------------------------------------

function scaleCapacityRow<T extends CapacityModelRow | PriorityCapacityRow>(
  row: T,
  multiplier: number,
  planningWeeks: number,
  capacityBufferPercent: number,
  defaultWorkUnitHours: number,
): T {
  const weeklyCapacityHours = row.weeklyCapacityHours * multiplier;
  // Re-derived from weekly hours with the same formula the loader uses
  // (`deriveCapacityFields`), not by scaling the derived value — the buffer
  // must not land twice.
  const effectiveCapacityHours = weeklyCapacityHours * planningWeeks * (1 - capacityBufferPercent);
  return {
    ...row,
    weeklyCapacityHours,
    effectiveCapacityHours,
    workUnitCapacity:
      defaultWorkUnitHours > 0 ? effectiveCapacityHours / defaultWorkUnitHours : 0,
  };
}

/**
 * One future: demand and capacity drawn, participation resolved.
 *
 * Exported because the most useful thing to be able to do with a simulation is
 * to inspect a single draw of it.
 */
export function buildSampledInput(
  input: PlanningEngineInput,
  config: MonteCarloConfig,
  rng: () => number,
): PlanningEngineInput {
  const { scenario } = input;
  const rho = Math.min(Math.max(config.correlation, 0), 1);
  const idiosyncratic = Math.sqrt(1 - rho * rho);

  // The shared factor. Positive = the future where things went badly: demand
  // ran over and pools were thinner than planned.
  const common = standardNormal(rng);
  const load = (z: number) => rho * common + idiosyncratic * z;

  const workUnits = input.workUnits.map((wu) => {
    const sigma = workUnitSpread(wu, config);
    const m = logNormalFrom(load(standardNormal(rng)), config.demandBias, sigma);
    if (m === 1) return wu;
    return {
      ...wu,
      effortEstimateHours: wu.effortEstimateHours * m,
      countedEffortHours: wu.countedEffortHours == null ? null : wu.countedEffortHours * m,
    };
  });

  const priorities = input.priorities.map((p) => {
    const sigma = prioritySpread(p, config);
    const m = logNormalFrom(load(standardNormal(rng)), config.demandBias, sigma);
    if (m === 1) return p;
    return {
      ...p,
      priorityLevelWuEstimate:
        p.priorityLevelWuEstimate == null ? null : p.priorityLevelWuEstimate * m,
      remainingWuEstimate: p.remainingWuEstimate == null ? null : p.remainingWuEstimate * m,
    };
  });

  // One availability draw per resource TYPE, not per row: a team being thinner
  // than planned is one event, however many pool rows describe it. Capacity
  // loads negatively on the shared factor — the bad future is bad on both
  // sides of the ledger at once.
  const poolMultiplier = new Map<string, number>();
  const poolMultiplierFor = (resourceType: string): number => {
    const existing = poolMultiplier.get(resourceType);
    if (existing != null) return existing;
    const m =
      config.capacitySpread <= 0
        ? 1
        : logNormalFrom(-load(standardNormal(rng)), 1, config.capacitySpread);
    poolMultiplier.set(resourceType, m);
    return m;
  };

  const { planningWeeks, capacityBufferPercent, defaultWorkUnitHours } = scenario;
  const capacityRows = input.capacityRows.map((row) =>
    scaleCapacityRow(
      row,
      poolMultiplierFor(row.resourceType),
      planningWeeks,
      capacityBufferPercent,
      defaultWorkUnitHours,
    ),
  );
  const priorityCapacityRows = input.priorityCapacityRows.map((row) =>
    scaleCapacityRow(
      row,
      poolMultiplierFor(row.resourceType),
      planningWeeks,
      capacityBufferPercent,
      defaultWorkUnitHours,
    ),
  );

  // Roster mode plans against qualified supply units, not the pool rows. Left
  // unscaled, capacity uncertainty would silently do nothing for exactly the
  // scenarios that model people most carefully.
  const qualification = input.qualification
    ? {
        ...input.qualification,
        supply: input.qualification.supply.map(
          (unit): QualifiedSupplyUnit => ({
            ...unit,
            workUnitCapacity: unit.workUnitCapacity * poolMultiplierFor(unit.resourceType),
          }),
        ),
      }
    : undefined;

  const teamParticipation = config.participationRisk
    ? sampleParticipation(input.teamParticipation, rng, common * 0.08)
    : input.teamParticipation;

  return {
    ...input,
    priorities,
    workUnits,
    capacityRows,
    priorityCapacityRows,
    teamParticipation,
    qualification,
  };
}

// ---------------------------------------------------------------------------
// §6 — Result shapes
// ---------------------------------------------------------------------------

export type MonteCarloBand = 'firm' | 'at_risk' | 'out';

export const MONTE_CARLO_BAND_LABELS: Record<MonteCarloBand, string> = {
  firm: 'Firm',
  at_risk: 'At risk',
  out: 'Out',
};

export interface MonteCarloPriorityRecord {
  priorityId: string;
  name: string;
  forceRank: number | null;
  /** Mean commitment size across trials, in work units. */
  meanRequiredWU: number;
  /** Share of futures where the priority carried at all (fully or partially). */
  inclusionProbability: number;
  /** Share of futures where every work unit of it carried. */
  fullInclusionProbability: number;
  /** Share of futures where its participation was flagged at risk. */
  participationRiskProbability: number;
  /** Coverage percentiles, 0–100. p10 is the bad future, p90 the good one. */
  coverageP10: number;
  coverageP50: number;
  coverageP90: number;
  band: MonteCarloBand;
  /** The reason that stopped it most often, across futures where it fell short. */
  topExclusionReason: string | null;
  /** Share of ALL trials in which that reason was the one recorded. */
  topExclusionReasonShare: number;
}

export interface MonteCarloDistribution {
  p10: number;
  p50: number;
  p80: number;
  p90: number;
  mean: number;
  min: number;
  max: number;
  /** Sorted samples — the histogram is drawn from these. */
  samples: number[];
}

export interface MonteCarloResourceRecord {
  resourceType: string;
  /** Utilization percentiles, 0–100+. p90 is the loaded future. */
  utilizationP50: number;
  utilizationP90: number;
  /** Share of futures where demand on this pool exceeded its capacity. */
  overSubscribedProbability: number;
}

export interface MonteCarloResult {
  config: MonteCarloConfig;
  iterations: number;
  priorityRecords: MonteCarloPriorityRecord[];
  /** Priorities carrying at all, per trial. */
  carriedCount: MonteCarloDistribution;
  /** Work units landing inside the plan, per trial. */
  carriedWorkUnits: MonteCarloDistribution;
  /** Total demand the portfolio presented, per trial. */
  requiredWorkUnits: MonteCarloDistribution;
  resourceRecords: MonteCarloResourceRecord[];
  firmCount: number;
  atRiskCount: number;
  outCount: number;
  generatedAt: string;
  elapsedMs: number;
}

function bandFor(inclusionProbability: number): MonteCarloBand {
  if (inclusionProbability >= FIRM_PROBABILITY) return 'firm';
  if (inclusionProbability < OUT_PROBABILITY) return 'out';
  return 'at_risk';
}

/**
 * Nearest-rank percentile on an already-sorted array.
 *
 * Nearest-rank rather than interpolated on purpose: every value it can return
 * is a future the simulation actually produced, so "P80 scope is 34 work units"
 * names a real trial rather than a number sitting between two of them.
 */
export function percentile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1));
  return sorted[idx];
}

function distribution(samples: number[]): MonteCarloDistribution {
  const sorted = [...samples].sort((a, b) => a - b);
  const mean = sorted.length ? sorted.reduce((sum, v) => sum + v, 0) / sorted.length : 0;
  return {
    p10: percentile(sorted, 0.1),
    p50: percentile(sorted, 0.5),
    p80: percentile(sorted, 0.8),
    p90: percentile(sorted, 0.9),
    mean,
    min: sorted[0] ?? 0,
    max: sorted[sorted.length - 1] ?? 0,
    samples: sorted,
  };
}

function carried(summary: PrioritySummary): boolean {
  return (
    summary.feasibility === 'Fully Achievable' || summary.feasibility === 'Partially Achievable'
  );
}

// ---------------------------------------------------------------------------
// §7 — The run
// ---------------------------------------------------------------------------

interface PriorityAccumulator {
  name: string;
  forceRank: number | null;
  requiredWUSum: number;
  carriedCount: number;
  fullCount: number;
  participationRiskCount: number;
  coverage: number[];
  reasons: Map<string, number>;
}

/**
 * A simulation in progress.
 *
 * Split into batches rather than one call because a thousand engine passes is
 * long enough to freeze a tab. The caller runs a slice, yields to the browser,
 * then runs the next — with the arithmetic staying pure and synchronous, so
 * tests drive it in one shot and never touch a timer.
 */
export class MonteCarloRun {
  readonly config: MonteCarloConfig;

  private readonly input: PlanningEngineInput;
  private readonly rng: () => number;
  private readonly priorities = new Map<string, PriorityAccumulator>();
  private readonly resources = new Map<string, { utilization: number[]; overCount: number }>();
  private readonly carriedCounts: number[] = [];
  private readonly carriedWU: number[] = [];
  private readonly requiredWU: number[] = [];
  private elapsedMs = 0;
  private completed = 0;

  constructor(input: PlanningEngineInput, config: MonteCarloConfig) {
    this.input = input;
    this.config = config;
    this.rng = createRng(config.seed);
  }

  get trialsCompleted(): number {
    return this.completed;
  }

  get progress(): number {
    return this.config.iterations === 0 ? 1 : this.completed / this.config.iterations;
  }

  get done(): boolean {
    return this.completed >= this.config.iterations;
  }

  /** Runs up to `count` more trials. Returns how many actually ran. */
  runBatch(count: number): number {
    const target = Math.min(this.completed + count, this.config.iterations);
    const started = this.completed;
    const batchStart = Date.now();
    while (this.completed < target) {
      this.recordTrial(runPlanningEngine(buildSampledInput(this.input, this.config, this.rng)));
      this.completed += 1;
    }
    this.elapsedMs += Date.now() - batchStart;
    return this.completed - started;
  }

  private accumulatorFor(summary: PrioritySummary): PriorityAccumulator {
    const id = summary.priority.id;
    let acc = this.priorities.get(id);
    if (!acc) {
      acc = {
        name: summary.priority.name,
        forceRank: summary.priority.forceRank,
        requiredWUSum: 0,
        carriedCount: 0,
        fullCount: 0,
        participationRiskCount: 0,
        coverage: [],
        reasons: new Map(),
      };
      this.priorities.set(id, acc);
    }
    return acc;
  }

  private recordTrial(output: PlanningEngineOutput): void {
    let trialCarried = 0;
    let trialCarriedWU = 0;
    let trialRequiredWU = 0;

    for (const summary of output.prioritySummaries) {
      const acc = this.accumulatorFor(summary);
      acc.requiredWUSum += summary.requiredWU;
      acc.coverage.push(summary.coveragePercent);
      if (carried(summary)) {
        acc.carriedCount += 1;
        trialCarried += 1;
      }
      if (summary.feasibility === 'Fully Achievable') acc.fullCount += 1;
      if (summary.hasParticipationRisk) acc.participationRiskCount += 1;

      trialRequiredWU += summary.requiredWU;
      trialCarriedWU += summary.includedWU;
    }

    // Why it fell short, counted from the records the engine already wrote —
    // at most one reason per priority per trial, so the tally reads as "in 61%
    // of futures the thing that stopped it was X".
    //
    // The first excluded work unit's reason stands for the priority (it is the
    // first thing that stopped it); a priority-grain record overwrites that,
    // since in Priority Estimate mode that record IS the decision.
    const reasonThisTrial = new Map<string, string>();
    for (const record of output.planRecords) {
      const id = record.priority?.id;
      if (!id || record.planDecision === 'Included') continue;
      if (!reasonThisTrial.has(id)) reasonThisTrial.set(id, record.decisionReason);
    }
    for (const record of output.priorityPlanRecords) {
      if (record.planDecision === 'Included') continue;
      reasonThisTrial.set(record.priority.id, record.decisionReason);
    }
    for (const [id, reason] of reasonThisTrial) {
      const acc = this.priorities.get(id);
      if (!acc || !reason) continue;
      acc.reasons.set(reason, (acc.reasons.get(reason) ?? 0) + 1);
    }

    for (const resource of output.resourceSummaries) {
      let acc = this.resources.get(resource.resourceType);
      if (!acc) {
        acc = { utilization: [], overCount: 0 };
        this.resources.set(resource.resourceType, acc);
      }
      acc.utilization.push(resource.utilizationPercent);
      if (resource.demandWU > resource.capacityWU) acc.overCount += 1;
    }

    this.carriedCounts.push(trialCarried);
    this.carriedWU.push(trialCarriedWU);
    this.requiredWU.push(trialRequiredWU);
  }

  finalize(): MonteCarloResult {
    const iterations = this.completed;
    const share = (n: number) => (iterations === 0 ? 0 : n / iterations);

    const priorityRecords: MonteCarloPriorityRecord[] = [];
    for (const [priorityId, acc] of this.priorities) {
      const sortedCoverage = [...acc.coverage].sort((a, b) => a - b);
      const inclusionProbability = share(acc.carriedCount);
      let topReason: string | null = null;
      let topCount = 0;
      for (const [reason, count] of acc.reasons) {
        if (count > topCount) {
          topReason = reason;
          topCount = count;
        }
      }
      priorityRecords.push({
        priorityId,
        name: acc.name,
        forceRank: acc.forceRank,
        meanRequiredWU: iterations === 0 ? 0 : acc.requiredWUSum / iterations,
        inclusionProbability,
        fullInclusionProbability: share(acc.fullCount),
        participationRiskProbability: share(acc.participationRiskCount),
        coverageP10: percentile(sortedCoverage, 0.1),
        coverageP50: percentile(sortedCoverage, 0.5),
        coverageP90: percentile(sortedCoverage, 0.9),
        band: bandFor(inclusionProbability),
        topExclusionReason: topReason,
        topExclusionReasonShare: share(topCount),
      });
    }

    priorityRecords.sort((a, b) => {
      const ar = a.forceRank ?? Number.MAX_SAFE_INTEGER;
      const br = b.forceRank ?? Number.MAX_SAFE_INTEGER;
      if (ar !== br) return ar - br;
      return b.inclusionProbability - a.inclusionProbability;
    });

    const resourceRecords: MonteCarloResourceRecord[] = [];
    for (const [resourceType, acc] of this.resources) {
      const sorted = [...acc.utilization].sort((a, b) => a - b);
      resourceRecords.push({
        resourceType,
        utilizationP50: percentile(sorted, 0.5),
        utilizationP90: percentile(sorted, 0.9),
        overSubscribedProbability: share(acc.overCount),
      });
    }
    resourceRecords.sort((a, b) => b.utilizationP90 - a.utilizationP90);

    return {
      config: this.config,
      iterations,
      priorityRecords,
      carriedCount: distribution(this.carriedCounts),
      carriedWorkUnits: distribution(this.carriedWU),
      requiredWorkUnits: distribution(this.requiredWU),
      resourceRecords,
      firmCount: priorityRecords.filter((r) => r.band === 'firm').length,
      atRiskCount: priorityRecords.filter((r) => r.band === 'at_risk').length,
      outCount: priorityRecords.filter((r) => r.band === 'out').length,
      generatedAt: new Date().toISOString(),
      elapsedMs: this.elapsedMs,
    };
  }
}

/**
 * Runs a whole simulation in one call.
 *
 * Fine for tests and small portfolios; the UI drives `MonteCarloRun` in batches
 * instead so the tab stays alive.
 */
export function runMonteCarlo(
  input: PlanningEngineInput,
  config: Partial<MonteCarloConfig> = {},
): MonteCarloResult {
  const merged = { ...DEFAULT_MONTE_CARLO_CONFIG, ...config };
  const run = new MonteCarloRun(input, merged);
  run.runBatch(merged.iterations);
  return run.finalize();
}

// ---------------------------------------------------------------------------
// §8 — Histogram
// ---------------------------------------------------------------------------

export interface HistogramBin {
  start: number;
  end: number;
  count: number;
  /** Share of trials in this bin, 0–1. */
  share: number;
}

/** Equal-width bins over a distribution's samples, for the outcome chart. */
export function histogram(dist: MonteCarloDistribution, binCount = 24): HistogramBin[] {
  const { samples } = dist;
  if (samples.length === 0) return [];
  const min = samples[0];
  const max = samples[samples.length - 1];
  if (max === min) {
    return [{ start: min, end: max, count: samples.length, share: 1 }];
  }
  const width = (max - min) / binCount;
  const bins: HistogramBin[] = Array.from({ length: binCount }, (_, i) => ({
    start: min + i * width,
    end: min + (i + 1) * width,
    count: 0,
    share: 0,
  }));
  for (const value of samples) {
    const idx = Math.min(binCount - 1, Math.floor((value - min) / width));
    bins[idx].count += 1;
  }
  for (const bin of bins) bin.share = bin.count / samples.length;
  return bins;
}
